import { CheckIcon, CopyIcon, InfoIcon } from "lucide-react";
import { FC, useEffect } from "react";
import { useIntl } from "react-intl";
import { Chat, Folder } from "~/api/model";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useCopyContent } from "~/hooks/useCopyContent";
import { trackEvent } from "~/utils/analytics";
import { formatDateOnly } from "~/utils/date";

type CaseInfoPopoverProps = {
  caseInfo: Folder;
  chatInfo: Chat;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  totalPages?: number;
  isMasterCase?: boolean;
};

export const CaseInfoPopover: FC<CaseInfoPopoverProps> = ({
  caseInfo,
  chatInfo,
  open,
  onOpenChange,
  totalPages,
  isMasterCase = true,
}) => {
  const titleValue = isMasterCase
    ? chatInfo.title
    : `${caseInfo.name} / ${chatInfo.title}`;

  const properties = [
    {
      messageId: "title",
      value: titleValue,
    },
    {
      messageId: "dateOfBirthLabel",
      value: formatDateOnly(caseInfo.dateOfBirth),
    },
    {
      messageId: "dateOfLossLabel",
      value: formatDateOnly(caseInfo.dateOfLoss),
    },
    {
      messageId: "claimNumberLabel",
      value: caseInfo.claimNumber ?? "-",
    },
    {
      messageId: "totalPagesLabel",
      value: totalPages?.toString() ?? "",
    },
  ];

  useEffect(() => {
    if (open) {
      trackEvent({ eventName: "app/doc_list/info.button/click" });
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger>
        <InfoIcon className="h-4 w-4" />
      </PopoverTrigger>
      <PopoverContent align="start" className="min-w-[320px] w-full z-[1000]">
        <div className="self-stretch flex-col justify-start items-start gap-2 flex">
          {properties
            .filter(
              (property) =>
                property.value &&
                property.value !== "-" &&
                property.value.trim() !== ""
            )
            .map((property) => (
              <CaseInfoPopoverItem
                property={property}
                key={property.messageId}
              />
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const CaseInfoPopoverItem = ({
  property,
}: {
  property: { messageId: string; value: string };
}) => {
  const { formatMessage } = useIntl();
  const { isCopied, handleCopy } = useCopyContent();

  const Icon = isCopied ? CheckIcon : CopyIcon;

  const onCopy = () => {
    trackEvent({
      eventName: "app/case_information_modal/copy.button/click",
      properties: { label: property.messageId },
    });
    handleCopy(property.value);
  };

  return (
    <div
      className="self-stretch group flex flex-col cursor-pointer text-zinc-500 hover:text-black"
      onClick={onCopy}
    >
      <div className="text-xs font-normal leading-none text-zinc-500">
        {formatMessage({ id: property.messageId })}
        {property.messageId !== "title" && ": "}
      </div>
      <div className="items-center gap-2 flex">
        <span className="text-sm font-semibold leading-tight text-zinc-900">
          {property.value}
        </span>
        <Icon
          className={`w-4 h-4 min-h-4 min-w-4 transition-opacity duration-200 opacity-0 group-hover:opacity-100 ${isCopied ? "text-green-500" : ""}`}
        />
      </div>
    </div>
  );
};
